/**
 * Ticket VS2: Übersicht der Aussendungen. Anlegen und Zustand je Lauf; der
 * Versand selbst geschieht in der Einzelansicht.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { adminInputClass } from "@/components/admin/AdminField";
import { createBroadcast, deleteBroadcast, listBroadcasts } from "@/lib/broadcasts.functions";
import {
  BROADCAST_DEFAULT_RUN_LIMIT,
  BROADCAST_PROCESSING_LABELS,
  BROADCAST_STATUS_LABELS,
  type BroadcastStatus,
} from "@/lib/broadcasts";

export const Route = createFileRoute("/_admin/admin/broadcasts/")({
  component: BroadcastsPage,
  head: () => ({ meta: [{ title: "Aussendungen – Interner Bereich" }] }),
});

function BroadcastsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const listFn = useServerFn(listBroadcasts);
  const createFn = useServerFn(createBroadcast);
  const deleteFn = useServerFn(deleteBroadcast);
  const [subject, setSubject] = useState("");

  const list = useQuery({
    queryKey: ["admin", "broadcasts"],
    queryFn: () => listFn(),
  });

  const create = useMutation({
    mutationFn: (value: string) =>
      createFn({
        data: {
          subject: value,
          bodyMd: "",
          filters: {},
          perRunLimit: BROADCAST_DEFAULT_RUN_LIMIT,
        },
      }),
    onSuccess: async (result) => {
      setSubject("");
      await queryClient.invalidateQueries({ queryKey: ["admin", "broadcasts"] });
      navigate({ to: "/admin/broadcasts/$id", params: { id: result.id } });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Anlegen fehlgeschlagen"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: async () => {
      toast.success("Aussendung gelöscht.");
      await queryClient.invalidateQueries({ queryKey: ["admin", "broadcasts"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Löschen fehlgeschlagen"),
  });

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Aussendungen"
        description="Nachricht verfassen, Empfängerkreis übernehmen und die gewählte Menge zuverlässig aussenden. Der Abmeldelink ist Bestandteil jeder Aussendung."
      />

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const value = subject.trim();
          if (!value) {
            toast.error("Bitte einen Betreff angeben.");
            return;
          }
          create.mutate(value);
        }}
      >
        <input
          aria-label="Betreff der neuen Aussendung"
          className={adminInputClass + " max-w-md flex-1"}
          placeholder="Betreff der neuen Aussendung"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
        />
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
        >
          {create.isPending ? "Wird angelegt …" : "Aussendung anlegen"}
        </button>
      </form>

      {list.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
      {list.isError ? (
        <p className="text-sm text-destructive">Die Aussendungen sind nicht verfügbar.</p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Aussendung</th>
              <th className="px-4 py-3">Stand</th>
              <th className="px-4 py-3 text-right">Versendet</th>
              <th className="px-4 py-3 text-right">Angekommen</th>
              <th className="px-4 py-3 text-right">Rückläufe</th>
              <th className="px-4 py-3 text-right">Abmeldungen</th>
              <th className="px-4 py-3 text-right">Klicks</th>
              <th className="px-4 py-3"><span className="sr-only">Aktionen</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(list.data ?? []).map((b) => (
              <tr key={b.id}>
                <td className="px-4 py-3">
                  <Link
                    to="/admin/broadcasts/$id"
                    params={{ id: b.id }}
                    className="font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    {b.subject}
                  </Link>
                  {b.status === "laeuft" ? (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {BROADCAST_PROCESSING_LABELS[b.processingState]}
                    </span>
                  ) : null}
                  {b.pauseReason ? (
                    <span className="ml-2 text-xs text-destructive">{b.pauseReason}</span>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={b.status} />
                </td>
                {b.frozenAt ? (
                  <>
                    <td className="px-4 py-3 text-right tabular-nums">{b.metrics.sent}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{b.metrics.arrived}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{b.metrics.bounced}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{b.metrics.unsubscribed}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {b.metrics.clicks === null ? (
                        <span title="Aussendung von vor der Klickzählung – nicht gemessen">–</span>
                      ) : (
                        b.metrics.clicks
                      )}
                    </td>
                  </>
                ) : (
                  <td className="px-4 py-3 text-muted-foreground" colSpan={5}>
                    Empfängerkreis noch nicht festgeschrieben
                  </td>
                )}
                <td className="px-4 py-3 text-right">
                  {b.status === "entwurf" && b.processingState === "idle" && <Button
                    type="button" size="icon" variant="ghost" title="Aussendung löschen"
                    aria-label={`Aussendung ${b.subject} löschen`}
                    disabled={remove.isPending}
                    onClick={() => { if (window.confirm(`Aussendung „${b.subject}“ endgültig löschen?`)) remove.mutate(b.id); }}
                  ><Trash2 className="size-4" /></Button>}
                </td>
              </tr>
            ))}
            {list.data && list.data.length === 0 ? (
              <tr>
                <td className="px-4 py-3 text-muted-foreground" colSpan={8}>
                  Es gibt noch keine Aussendung.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StatusBadge({ status }: { status: BroadcastStatus }) {
  const tone =
    status === "angehalten"
      ? "bg-destructive/10 text-destructive"
      : status === "abgeschlossen"
        ? "bg-primary/10 text-primary"
        : "bg-muted text-muted-foreground";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {BROADCAST_STATUS_LABELS[status] ?? status}
    </span>
  );
}
